/**
 * Background animation featuring subtle wave-like grid movement
 */

interface Point {
    x: number;
    y: number;
    baseY: number;
}

export class BackgroundAnimation {
    private canvas: HTMLCanvasElement;
    private ctx: CanvasRenderingContext2D;
    private points: Point[] = [];
    private animationFrameId: number | null = null;
    private width: number = 0;
    private height: number = 0;

    // Configuration
    private readonly gridSize = 36; // Spacing between points
    private readonly waveAmplitude = 16; // Vertical movement amount
    private readonly waveFrequency = 0.01; // How tight the waves are
    private readonly speed = 0.0005; // Animation speed
    private readonly rectSize = 3; // Size of the rectangles

    constructor() {
        this.canvas = document.createElement('canvas');
        this.ctx = this.canvas.getContext('2d')!;

        this.init();
    }

    private init(): void {
        // Setup canvas styles
        this.canvas.style.position = 'fixed';
        this.canvas.style.top = '0';
        this.canvas.style.left = '0';
        this.canvas.style.width = '100%';
        this.canvas.style.height = '100%';
        this.canvas.style.zIndex = '-1';
        this.canvas.style.pointerEvents = 'none';
        this.canvas.style.opacity = '0.35'; // Subtle transparency

        document.body.prepend(this.canvas);

        // Bind methods
        this.resize = this.resize.bind(this);
        this.animate = this.animate.bind(this);

        // Initial setup
        window.addEventListener('resize', this.resize);
        this.resize();
        this.start();
    }

    private resize(): void {
        this.width = window.innerWidth;
        this.height = window.innerHeight;

        // Handle DPI
        const dpr = window.devicePixelRatio || 1;
        this.canvas.width = this.width * dpr;
        this.canvas.height = this.height * dpr;
        this.ctx.scale(dpr, dpr);

        this.createGrid();
    }

    private createGrid(): void {
        this.points = [];
        const cols = Math.ceil(this.width / this.gridSize) + 2;
        const rows = Math.ceil(this.height / this.gridSize) + 2;

        // Center the grid
        const startX = (this.width - (cols - 1) * this.gridSize) / 2;
        const startY = (this.height - (rows - 1) * this.gridSize) / 2;

        for (let i = 0; i < cols; i++) {
            for (let j = 0; j < rows; j++) {
                this.points.push({
                    x: startX + i * this.gridSize,
                    y: startY + j * this.gridSize,
                    baseY: startY + j * this.gridSize
                });
            }
        }
    }

    private start(): void {
        if (!this.animationFrameId) {
            this.animate(0);
        }
    }

    private animate(timestamp: number): void {
        this.ctx.clearRect(0, 0, this.width, this.height);

        // Style for the rectangles
        this.ctx.fillStyle = '#1A1A1A'; // Using brand foreground color
        this.ctx.globalAlpha = 0.2; // Increased visibility

        const time = timestamp * this.speed;

        this.points.forEach(point => {
            // Calculate wave displacement
            // Combining sine waves on both axes for organic sea-like movement
            const displacement =
                Math.sin(point.x * this.waveFrequency + time) *
                Math.cos(point.y * this.waveFrequency + time) *
                this.waveAmplitude;

            // Draw rectangle
            this.ctx.fillRect(
                point.x - this.rectSize / 2,
                (point.y + displacement) - this.rectSize / 2,
                this.rectSize,
                this.rectSize
            );
        });

        this.animationFrameId = requestAnimationFrame(this.animate);
    }

    public destroy(): void {
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
        }
        window.removeEventListener('resize', this.resize);
        this.canvas.remove();
    }
}
